// Hooks de React Query pro domínio ClientContact.
// - useContacts — lista de contatos do cliente (filtros is_active, role)
// - useCreateContact / useUpdateContact / useDeactivateContact / useReactivateContact

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateClientContactDto,
  QueryClientContactsDto,
  UpdateClientContactDto,
} from '@control-center/shared';

import {
  createContact,
  fetchContacts,
  updateContact,
  type ContactEntity,
} from '../lib/contacts-api';

export const contactsKeys = {
  all: ['contacts'] as const,
  byClient: (clientId: string) => [...contactsKeys.all, clientId] as const,
  list: (clientId: string, filters: QueryClientContactsDto) =>
    [...contactsKeys.byClient(clientId), 'list', filters] as const,
};

export function useContacts(clientId: string, filters: QueryClientContactsDto = {}) {
  return useQuery<ContactEntity[]>({
    queryKey: contactsKeys.list(clientId, filters),
    queryFn: () => fetchContacts(clientId, filters),
    enabled: Boolean(clientId),
    staleTime: 30_000,
  });
}

export function useCreateContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClientContactDto) => createContact(clientId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactsKeys.byClient(clientId) });
    },
  });
}

export function useUpdateContact(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, payload }: { contactId: string; payload: UpdateClientContactDto }) =>
      updateContact(clientId, contactId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactsKeys.byClient(clientId) });
    },
  });
}
